<template>
  <main id="main" class="main">
    <div class="pagetitle">
      <h1>USER PROFILE</h1>
      <nav>
        <ol class="breadcrumb breadJa">
          <router-link :to="{ name: 'home' }" class="breadcrumb-item">Home</router-link>
          <router-link :to="{ name: 'profile' }" class="breadcrumb-item active">My Profile</router-link>
        </ol>
      </nav>
    </div>

    <section class="section">
      <div class="row g-4">
        <!-- Profile Information Card -->
        <div class="col-lg-6">
          <div class="card profile-card-system h-100">
            <div class="card-body pt-3">
              <h5 class="profile-title-system">
                <i class="bi bi-person-circle me-2"></i>
                Profile Information
              </h5>

              <form @submit.prevent="handleUpdateProfile">
                <div class="avatar-section-system mb-4">
                  <div class="d-flex align-items-center flex-wrap flex-md-nowrap gap-4">
                    <!-- Avatar Upload Component -->
                    <div class="avatar-upload-container position-relative flex-shrink-0" @click="triggerFileInput">
                      <input 
                        ref="fileInput"
                        type="file" 
                        accept="image/jpeg,image/png,image/webp"
                        @change="handleFileSelect"
                        style="display: none;"
                      />
                      <div class="avatar-wrapper">
                        <img 
                          v-if="avatarPreview || profileData.avatar_url"
                          :src="avatarPreview || getFullAvatarUrl(profileData.avatar_url)" 
                          alt="Profile Avatar"
                          @error="handleImageError"
                          class="avatar-image"
                        />
                        <div v-else class="avatar-initials">
                          {{ getUserInitials() }}
                        </div>
                        <div class="avatar-overlay">
                          <i class="bi bi-camera-fill"></i>
                        </div>
                        <div v-if="isUploadingAvatar" class="avatar-loading">
                          <div class="spinner-border spinner-border-sm text-light" role="status"></div>
                        </div>
                      </div>
                    </div>

                    <div class="flex-grow-1">
                      <h4 class="mb-1 fw-bold text-dark">{{ profileData.full_name || user?.username }}</h4>
                      <p class="mb-0 text-muted small">
                        <span class="badge" :class="user?.role === 'admin' ? 'bg-danger-light text-danger border-danger' : 'bg-primary-light text-primary border-primary'" style="border: 0.5px solid">
                          <i class="bi bi-shield-lock me-1"></i>{{ user?.role?.toUpperCase() }}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group-profile">
                      <label>Username</label>
                      <div class="input-with-icon-system">
                        <i class="bi bi-person-fill field-icon"></i>
                        <input type="text" :value="user?.username" disabled>
                      </div>
                      <div class="form-info-text-system">
                        <i class="bi bi-info-circle"></i>
                        <span>Username cannot be changed</span>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="form-group-profile">
                      <label>Email Address</label>
                      <div class="input-with-icon-system">
                        <i class="bi bi-envelope-fill field-icon"></i>
                        <input type="email" :value="user?.email" disabled>
                      </div>
                      <div class="form-info-text-system">
                        <i class="bi bi-info-circle"></i>
                        <span>Email cannot be changed</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="form-group-profile">
                  <label>Full Name</label>
                  <div class="input-with-icon-system">
                    <i class="bi bi-person-badge-fill field-icon"></i>
                    <input
                      type="text"
                      id="full_name"
                      v-model="profileData.full_name"
                      :disabled="!isEditing"
                      placeholder="Enter your full name"
                    >
                  </div>
                </div>

                <div class="text-center mt-2">
                  <div v-if="!isEditing">
                    <button type="button" @click="startEditing" class="btn-premium-profile shadow-sm w-100">
                      <i class="bi bi-pencil me-2"></i>Edit Profile
                    </button>
                  </div>

                  <div v-else class="d-flex gap-2">
                    <button type="submit" class="btn-premium-profile flex-fill" :disabled="isLoading">
                      <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
                      <i v-else class="bi bi-check-lg me-2"></i>
                      Save Changes
                    </button>
                    <button type="button" @click="cancelEditing" class="btn btn-outline-secondary px-4 border-2" style="border-radius: 8px;">
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <!-- Security Card -->
        <div class="col-lg-6">
          <div class="card profile-card-system h-100">
            <div class="card-body pt-3">
              <h5 class="profile-title-system">
                <i class="bi bi-shield-lock me-2"></i>
                Security Settings
              </h5>

              <form @submit.prevent="handleChangePassword">
                <div class="form-group-profile">
                  <label>Current Password</label>
                  <div class="input-with-icon-system">
                    <i class="bi bi-key-fill field-icon"></i>
                    <input
                      :type="showOldPassword ? 'text' : 'password'"
                      id="old_password"
                      v-model="passwordData.old_password"
                      placeholder="Enter current password"
                    >
                    <button 
                      type="button" 
                      class="password-toggle-system"
                      @click="showOldPassword = !showOldPassword"
                    >
                      <i :class="showOldPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                    </button>
                  </div>
                </div>

                <div class="form-group-profile">
                  <label>New Password</label>
                  <div class="input-with-icon-system">
                    <i class="bi bi-lock-fill field-icon"></i>
                    <input
                      :type="showNewPassword ? 'text' : 'password'"
                      id="new_password"
                      v-model="passwordData.new_password"
                      placeholder="Minimum 8 characters"
                      @input="checkPasswordStrength"
                    >
                    <button 
                      type="button" 
                      class="password-toggle-system"
                      @click="showNewPassword = !showNewPassword"
                    >
                      <i :class="showNewPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                    </button>
                  </div>
                  
                  <!-- Password Strength -->
                  <div v-if="passwordData.new_password" class="mt-2">
                    <div class="progress" style="height: 4px; background-color: rgba(1, 71, 112, 0.05);">
                      <div 
                        class="progress-bar" 
                        :class="{
                          'bg-danger': passwordStrength === 'weak',
                          'bg-warning': passwordStrength === 'medium',
                          'bg-success': passwordStrength === 'strong'
                        }"
                        :style="{ width: passwordStrength === 'weak' ? '33%' : passwordStrength === 'medium' ? '66%' : '100%' }"
                      ></div>
                    </div>
                    <small class="fw-bold" :class="{'text-danger': passwordStrength === 'weak','text-warning': passwordStrength === 'medium','text-success': passwordStrength === 'strong'}">
                      {{ passwordStrength.toUpperCase() }}
                    </small>
                  </div>
                </div>

                <div class="form-group-profile">
                  <label>Confirm Password</label>
                  <div class="input-with-icon-system">
                    <i class="bi bi-shield-check field-icon"></i>
                    <input
                      :type="showConfirmPassword ? 'text' : 'password'"
                      id="confirm_password"
                      v-model="confirmNewPassword"
                      placeholder="Re-enter new password"
                    >
                    <button 
                      type="button" 
                      class="password-toggle-system"
                      @click="showConfirmPassword = !showConfirmPassword"
                    >
                      <i :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                    </button>
                  </div>
                  <div v-if="passwordMismatch" class="text-danger small mt-1 fw-bold">
                    <i class="bi bi-exclamation-circle me-1"></i>Passwords do not match
                  </div>
                </div>

                <div class="alert bg-warning-light text-dark border-0 d-flex align-items-center mb-4" style="border-radius: 10px;">
                  <i class="bi bi-exclamation-triangle-fill text-warning fs-4 me-3"></i>
                  <small class="fw-medium">Changing your password will automatically log you out for security reasons.</small>
                </div>

                <button 
                  type="submit" 
                  class="btn-premium-profile w-100 shadow-sm" 
                  :disabled="isLoading || !isPasswordFormValid"
                >
                  <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
                  <i v-else class="bi bi-shield-check me-2"></i>
                   Change Password
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <!-- Theme Customization Section -->
      <div class="row g-4 mt-2">
        <div class="col-12">
          <div class="card profile-card-system">
            <div class="card-body pt-3">
              <h5 class="profile-title-system">
                <i class="bi bi-palette me-2"></i>
                Theme Customization
              </h5>

              <div class="palette-container-system">
                <div class="palette-header mb-4">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="fw-bold mb-0">Color palette</h6>
                  </div>
                </div>

                <div class="palette-subgroup mb-4">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <span class="text-muted small fw-bold text-uppercase">Preset palettes</span>
                  </div>

                  <div class="palette-grid">
                    <div 
                      v-for="(palette, index) in presetPalettes" 
                      :key="index"
                      class="palette-card"
                      :class="{ 'active': selectedPaletteIndex === index }"
                      @click="selectPalette(index)"
                    >
                      <div class="palette-colors">
                        <div 
                          v-for="(color, colIndex) in palette.colors" 
                          :key="colIndex"
                          class="color-block"
                          :style="{ backgroundColor: color }"
                        ></div>
                      </div>
                      <div class="palette-check" v-if="selectedPaletteIndex === index">
                        <i class="bi bi-check-circle-fill"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="text-end mt-4">
                  <button 
                    @click="saveTheme" 
                    class="btn-premium-profile px-5"
                    :disabled="selectedPaletteIndex === null"
                  >
                    <i class="bi bi-save me-2"></i>
                    Save to workspace
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { useSystem } from '@/composables/useSystem.js';
import { getPasswordStrength, validatePassword } from '@/utils/validators';
import { showConfirm, showSuccess, showError } from '@/utils/toast';

export default {
  name: 'ProfileView',

  setup() {
    const { user, isLoading, fetchProfile, updateUserProfile, changeUserPassword, uploadAvatar } = useAuth();
    const { setThemePalette, currentPalette, themeColor } = useSystem();

    const isEditing = ref(false);
    const profileData = ref({
      full_name: '',
      avatar_url: ''
    });

    // Avatar upload states
    const fileInput = ref(null);
    const avatarPreview = ref(null);
    const isUploadingAvatar = ref(false);
    const selectedFile = ref(null);

    const passwordData = ref({
      old_password: '',
      new_password: ''
    });

    const confirmNewPassword = ref('');
    const showOldPassword = ref(false);
    const showNewPassword = ref(false);
    const showConfirmPassword = ref(false);
    const passwordStrength = ref('weak');

    // Palette Data
    const selectedPaletteIndex = ref(null);
    const presetPalettes = ref([
      // --- SOFT PALETTES (9) ---
      { name: 'Classic Blue', colors: ['#014770', '#0489d6', '#a5d1eb', '#f0f7ff'] },
      { name: 'Soft Sage', colors: ['#5b8c5a', '#8fb98e', '#dcf1dc', '#f7faf7'] },
      { name: 'Muted Rose', colors: ['#a35d6a', '#d18e9a', '#fadedb', '#fff9f9'] },
      { name: 'Peaceful Teal', colors: ['#2d7a78', '#5ca19f', '#d5eeee', '#f4fbfb'] },
      { name: 'Dusty Lilac', colors: ['#7d6b91', '#a997bd', '#e9e3f0', '#f8f7fa'] },
      { name: 'Soft Coral', colors: ['#cc7a66', '#e6ae9f', '#fceae6', '#fffaf9'] },
      { name: 'Muted Indigo', colors: ['#4a5c8c', '#7a8bb8', '#e5eaf8', '#f5f7fc'] },
      { name: 'Sage Mist', colors: ['#778b80', '#a4b5ad', '#e7edeb', '#f6f9f8'] },
      { name: 'Warm Sand', colors: ['#8c7b64', '#b5a68d', '#f0ece3', '#f9f8f4'] },

      // --- STRONG PALETTES (9) ---
      { name: 'Deep Ocean', colors: ['#003366', '#0055bb', '#cce0ff', '#f0f5ff'] },
      { name: 'Forest Green', colors: ['#1b4d3e', '#2a8a5e', '#d1f2eb', '#f0f9f7'] },
      { name: 'Royal Ruby', colors: ['#9b111e', '#e31a2a', '#f9d5d8', '#fff5f6'] },
      { name: 'Electric Indigo', colors: ['#3f00ff', '#7b4cff', '#e6e0ff', '#f5f2ff'] },
      { name: 'Midnight Teal', colors: ['#004d4d', '#00a8a8', '#ccffff', '#f0ffff'] },
      { name: 'Burning Orange', colors: ['#e65100', '#ff9800', '#fff3e0', '#fffaf2'] },
      { name: 'Gothic Purple', colors: ['#301934', '#6a1b9a', '#e6ccff', '#f9f2ff'] },
      { name: 'Vibrant Magenta', colors: ['#c2185b', '#e91e63', '#f8bbd0', '#fce4ec'] },
      { name: 'Charcoal Pro', colors: ['#121212', '#424242', '#e0e0e0', '#f5f5f5'] }
    ]);

    const passwordMismatch = computed(() => {
      return confirmNewPassword.value && 
             passwordData.value.new_password !== confirmNewPassword.value;
    });

    const isPasswordFormValid = computed(() => {
      return passwordData.value.old_password &&
             passwordData.value.new_password &&
             confirmNewPassword.value &&
             validatePassword(passwordData.value.new_password) &&
             passwordData.value.new_password === confirmNewPassword.value;
    });

    function checkPasswordStrength() {
      passwordStrength.value = getPasswordStrength(passwordData.value.new_password);
    }

    function startEditing() {
      isEditing.value = true;
      profileData.value.full_name = user.value?.full_name || '';
      profileData.value.avatar_url = user.value?.avatar_url || '';
    }

    function cancelEditing() {
      isEditing.value = false;
      profileData.value.full_name = user.value?.full_name || '';
      profileData.value.avatar_url = user.value?.avatar_url || '';
    }

    async function handleUpdateProfile() {
      const result = await updateUserProfile(profileData.value);
      if (result.success) {
        isEditing.value = false;
      }
    }

    async function handleChangePassword() {
      if (passwordMismatch.value) {
        return;
      }

      const confirmed = await showConfirm(
        'Change Password?',
        'You will be logged out and need to sign in again with your new password.'
      );

      if (!confirmed) return;

      await changeUserPassword(
        passwordData.value.old_password,
        passwordData.value.new_password
      );
    }

    function handleImageError(event) {
      if (!event.target.dataset.errorHandled) {
        event.target.dataset.errorHandled = 'true';
        event.target.style.display = 'none';
      }
    }

    function getUserInitials() {
      const name = user.value?.full_name || user.value?.username || 'U';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }

    function getFullAvatarUrl(url) {
      if (!url) return '';
      if (url.startsWith('http')) return url;
      const baseUrl = process.env.VUE_APP_BE_APP_BASE_URL;
      return `${baseUrl}${url}?t=${new Date().getTime()}`;
    }

    function triggerFileInput() {
      fileInput.value?.click();
    }

    function handleFileSelect(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        showError('Please upload JPG, PNG, or WebP');
        return;
      }

      if (file.size > 1048576) {
        showError('Max file size is 1MB');
        return;
      }

      selectedFile.value = file;
      const reader = new FileReader();
      reader.onload = async (e) => {
        avatarPreview.value = e.target.result;
        isUploadingAvatar.value = true;
        const result = await uploadAvatar(selectedFile.value);
        if (result.success) {
          profileData.value.avatar_url = result.data.avatar_url;
          avatarPreview.value = null;
          selectedFile.value = null;
          showSuccess('Your profile picture has been updated');
        } else {
          avatarPreview.value = null;
          selectedFile.value = null;
        }
        isUploadingAvatar.value = false;
        if (fileInput.value) fileInput.value.value = '';
      };
      reader.readAsDataURL(file);
    }

    function selectPalette(index) {
      selectedPaletteIndex.value = index;
    }

    async function saveTheme() {
      if (selectedPaletteIndex.value !== null) {
        const palette = presetPalettes.value[selectedPaletteIndex.value];
        setThemePalette(palette);
        showSuccess(`Theme successfully updated to ${palette.name}`);
      }
    }

    onMounted(async () => {
      await fetchProfile();
      profileData.value.full_name = user.value?.full_name || '';
      profileData.value.avatar_url = user.value?.avatar_url || '';

      // Initialize selected palette if possible
      if (currentPalette.value) {
        const index = presetPalettes.value.findIndex(p => p.colors[0] === currentPalette.value.colors[0]);
        if (index !== -1) selectedPaletteIndex.value = index;
      }
    });

    return {
      user,
      isLoading,
      isEditing,
      profileData,
      passwordData,
      confirmNewPassword,
      showOldPassword,
      showNewPassword,
      showConfirmPassword,
      passwordStrength,
      passwordMismatch,
      isPasswordFormValid,
      startEditing,
      cancelEditing,
      handleUpdateProfile,
      handleChangePassword,
      handleImageError,
      checkPasswordStrength,
      fileInput,
      avatarPreview,
      isUploadingAvatar,
      triggerFileInput,
      handleFileSelect,
      getUserInitials,
      getFullAvatarUrl,
      presetPalettes,
      selectedPaletteIndex,
      selectPalette,
      saveTheme
    };
  }
};
</script>
